import { useEffect, useImperativeHandle, useRef, useState, forwardRef, type PointerEvent } from 'react';

export interface SignaturePadHandle {
  toDataURL: () => string | null;     // null if pad is empty
  clear: () => void;
  isEmpty: () => boolean;
}

type Props = {
  width?: number;
  height?: number;
  className?: string;
};

/**
 * Tiny touch/mouse-friendly signature canvas. Captures pen strokes via
 * pointer events, exports as PNG data URL. No external deps.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { width = 600, height = 180, className = '' },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Backing-store resolution for crisp lines on Retina.
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
  }, [width, height]);

  function point(e: PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current;
    if (!c) return null;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function onDown(e: PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const p = point(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPtRef.current = p;
    setIsEmpty(false);
  }
  function onMove(e: PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const p = point(e);
    if (!p) return;
    const ctx = canvasRef.current?.getContext('2d');
    const last = lastPtRef.current;
    if (!ctx || !last) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
  }
  function onUp(e: PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    lastPtRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }

  useImperativeHandle(ref, () => ({
    toDataURL: () => {
      if (isEmpty) return null;
      return canvasRef.current?.toDataURL('image/png') ?? null;
    },
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.restore();
      setIsEmpty(true);
    },
    isEmpty: () => isEmpty,
  }), [isEmpty]);

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      style={{ width, height, touchAction: 'none' }}
      className={'border-2 border-dashed border-gray-300 rounded bg-white cursor-crosshair ' + className}
      aria-label="Signature pad"
    />
  );
});
